import React, { useState } from "react";
import ListViewActions from "../../shared/ListViewActions";
import { GenericTable } from "@shared/components/GenericTable";
import { TableAction } from "@shared/types/table";
import { useSearchParams } from "react-router-dom";
import { useValidationContext } from "@features/mx/validations/context";
import { commonHeaders } from "@features/mx/validations/types";
import { useReadingsList, usePutRevertReading } from "@features/mx/validations/hooks";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { PAGE_SIZE } from "@shared/utils/constants";
import { downloadExcelSheet } from "@features/mx/validations/api";
import { useTableSort } from "@features/mx/utils/useTableSort";
import { logEvent } from "@shared/analytics/analytics";
import { useToast } from "@shared/hooks/use-toast";
import { RevertReadingModal } from "./RevertReading";

function PassReadingList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchValue, setSearchValue] = useState("");
  const [selectedPremises, setSelectedPremises] = useState<string[]>([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [revertModalOpen, setRevertModalOpen] = useState(false);
  const [revertRowData, setRevertRowData] = useState<any>(null);

  const { sortArray, sortConfig, handleSort } = useTableSort();

  const { setViewMode, meterRunId, setValidatorId, readingId } = useValidationContext();
  const remoteUtilityId = useRemoteUtilityId();
  const {toast} = useToast();
  const revertReading = usePutRevertReading(revertRowData?.id);
  const { data: pendingReadingList } = useReadingsList({
    remoteUtilityId,
    meterRunId,
    page: currentPage,
    limit: PAGE_SIZE,
    ruleStatus: "PASS",
    searchData: searchValue,
    premises: selectedPremises,
    sortBy: sortArray,
    readingId
  });

  const columns = React.useMemo(() => {
    return commonHeaders.map((col) => {
      if (col.key === "ruleStatus") {
        return {
          ...col,
          key: "actualStatus",
        };
      }
      return col;
    });
  }, []);

  const handleExport = async () => {
    try {
      const res = await downloadExcelSheet({
        remoteUtilityId,
        meterRunId,
        fileName: "break_view_readings",
        ruleStatus: "PASS",
      });
      toast({
          title: "Export Initiated",
          description: res?.data?.message,
          variant: "default",
        });
      logEvent('MX Reading List Exported', {
        type: 'Passed Readings',
        count: pendingReadingList?.count,
      })
    } catch (error) {
      toast({title: "Failed to download Excel", variant: 'destructive'});
    }
  };

  const actions: TableAction[] = [
    {
      label: "View",
      icon: "Eye",
      onClick: (row) => {
        // const hasValidatorIds =
        //   Array.isArray(row?.validatorIds) && row.validatorIds.length > 0;

        // if (hasValidatorIds) {
          const params = new URLSearchParams(searchParams);
          params.set("readingId", row?.id);
          setSearchParams(params);
          setViewMode("detail");
          setValidatorId(row.validatorIds);
        // } else {
        //   toast({title: "Validator not assigned for this reading"});
        //   setValidatorId(null);
        // }
      },
      variant: "ghost",
    },
    {
      label: "Revert",
      icon: "RotateCcw",
      onClick: (row) => {
        setRevertRowData({
          id: row?.id,
          resourceId: row?.meterNumber || "N/A",
          serialNo: row?.deviceNumber || "N/A",
          consumerName: row?.consumerName || "N/A",
          currentReading: row?.meterReading ?? "N/A",
        });
        setRevertModalOpen(true);
      },
      variant: "ghost",
    },
  ];

  const pagination = {
    currentPage,
    totalPages: Math.ceil((pendingReadingList?.count || 0) / PAGE_SIZE),
    totalItems: pendingReadingList?.count || 0,
    itemsPerPage: PAGE_SIZE,
    startItem: (currentPage - 1) * PAGE_SIZE + 1,
    endItem: Math.min(currentPage * PAGE_SIZE, pendingReadingList?.count || 0),
  };

  return (
    <div className="space-y-4">
      <ListViewActions
        onExportClick={handleExport}
        onSearch={(val) => setSearchValue(val)}
        onRouteChange={(val) => setSelectedPremises(val)}
      />
      {pendingReadingList?.results && (
        <GenericTable
          data={pendingReadingList.results}
          columns={columns}
          rowKey="id"
          onSelectionChange={setSelectedRows}
          actions={actions}
          actionsType="icons"
          pagination={pagination}
          onPageChange={setCurrentPage}
          sortConfig={sortConfig}
          onSort={handleSort}
          emptyMessage="No readings found"
        />
      )}
      <RevertReadingModal
        open={revertModalOpen}
        onClose={() => setRevertModalOpen(false)}
        onConfirm={() => {
          revertReading.mutate(undefined, {
            onSuccess: () => {
              setRevertModalOpen(false);
              toast({ title: "Reading reverted successfully" });
            },
          });
        }}
        loading={revertReading.isPending}
        data={revertRowData}
      />
    </div>
  );
}

export default PassReadingList;
